/**
 * Story 061 — Cascading Turbine Autocomplete.
 *
 * Single-select. Options are sourced from Story 057's `listTurbines`
 * endpoint scoped to the parent Park (`?site=<id>&pageSize=200`). The
 * option label is the composed `{internalParkNumber || "#" + assetCode} — {modelName}`
 * (since `listTurbines` doesn't return a server-composed
 * `concatenatedName`, the FE composes locally).
 *
 * Used by:
 *   - Add component modal (cascades from Park; disabled until Park).
 *   - Edit component modal (`aria-disabled` for non-tenant_admin actors
 *     with the explanatory tooltip — see EditComponentDialog).
 */
import { Autocomplete, TextField } from "@mui/material";
import type { ReactElement } from "react";
import { useReadOnlyContext } from "@aeriosiq/ui";
import type { Turbine } from "../../turbines/queries/index.js";

export interface TurbineOption {
  id: string;
  label: string;
  modelName: string;
}

/** Compose a single-line label for a Turbine row in the cascading picker. */
export function composeTurbineOptionLabel(t: Turbine): string {
  const tag =
    t.internalParkNumber !== null && t.internalParkNumber.length > 0
      ? t.internalParkNumber
      : `#${String(t.assetCode)}`;
  return `${tag} — ${t.modelName}`;
}

export function turbinesToOptions(
  turbines: readonly Turbine[],
): TurbineOption[] {
  return turbines.map((t) => ({
    id: t.id,
    label: composeTurbineOptionLabel(t),
    modelName: t.modelName,
  }));
}

export interface TurbineAutocompleteProps {
  value: string | null;
  options: readonly TurbineOption[];
  onChange: (id: string | null) => void;
  disabled?: boolean;
  required?: boolean;
  loading?: boolean;
  /** When true, render the input as `aria-disabled` rather than the
   *  fully-disabled form-control state — used by EditComponentDialog
   *  for the role-narrowed Turbine field on non-tenant_admin actors. */
  ariaDisabled?: boolean;
  label?: string;
}

export function TurbineAutocomplete({
  value,
  options,
  onChange,
  disabled = false,
  required = false,
  loading = false,
  ariaDisabled = false,
  label = "Turbine",
}: TurbineAutocompleteProps): ReactElement {
  const selected = options.find((o) => o.id === value) ?? null;
  // SB2-CC-03 — forward read-only state from `<ReadOnlyModal>` context.
  const { readOnly } = useReadOnlyContext();
  return (
    <Autocomplete<TurbineOption>
      options={[...options]}
      value={selected}
      disabled={disabled}
      readOnly={readOnly}
      loading={loading}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.label}
      onChange={(_e, next) => {
        onChange(next?.id ?? null);
      }}
      renderInput={(params) => {
        /* eslint-disable @typescript-eslint/no-explicit-any */
        const passThrough: any = params;
        return (
          <TextField
            {...passThrough}
            label={label}
            required={required}
            slotProps={{
              htmlInput: {
                ...params.inputProps,
                "aria-label": label,
                ...(required ? { "aria-required": true } : {}),
                ...(ariaDisabled
                  ? { "aria-disabled": true, readOnly: true }
                  : {}),
              },
            }}
            fullWidth
          />
        );
        /* eslint-enable */
      }}
    />
  );
}
